import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '../../../../alert/alert';
import { FairPriceChart } from '../../../chart/fair-price-chart';
import { FairPriceVerdict } from '../../../verdict/fair-price-verdict';

interface FairPriceTabDcfResultsProps {
    currency: string;
    currentPrice: number;
    intrinsicValue: number;
    fairPrice: number;
    upsideDownside: number;
    terminalValueWeight: number;
    isInvalidRate: boolean;
    showResult: boolean;
}

export const FairPriceTabDcfResults: React.FC<FairPriceTabDcfResultsProps> = ({
    currency,
    currentPrice,
    intrinsicValue,
    fairPrice,
    upsideDownside,
    terminalValueWeight,
    isInvalidRate,
    showResult,
}) => {
    const { t } = useTranslation();

    return (
        <>
            {isInvalidRate && (
                <Alert type={'warning'} icon>
                    {t('layout.dialogs.fairPrice.dcf.discountRateTooLow')}
                </Alert>
            )}
            {showResult && (
                <FairPriceVerdict
                    currency={currency}
                    values={[
                        {
                            label: t('layout.dialogs.fairPrice.dcf.intrinsicValue'),
                            value: intrinsicValue,
                        },
                        {
                            label: t('layout.dialogs.fairPrice.dcf.withMos'),
                            value: fairPrice,
                        },
                        {
                            label: t('layout.dialogs.fairPrice.result.currentPrice'),
                            value: currentPrice,
                        },
                        ...(terminalValueWeight > 0
                            ? [
                                  {
                                      label: t('layout.dialogs.fairPrice.dcf.terminalValueWeight'),
                                      value: terminalValueWeight,
                                      format: 'percent' as const,
                                  },
                              ]
                            : []),
                    ]}
                    pins={[
                        {
                            label: t('layout.dialogs.fairPrice.dcf.withMos'),
                            value: fairPrice,
                            variant: 'mos',
                        },
                        {
                            label: t('layout.dialogs.fairPrice.result.currentPrice'),
                            value: currentPrice,
                            variant: 'current',
                        },
                        {
                            label: t('layout.dialogs.fairPrice.dcf.intrinsicValue'),
                            value: intrinsicValue,
                            variant: 'fair',
                        },
                    ]}
                    upsideDownside={upsideDownside}
                    priceDiff={fairPrice - currentPrice}
                    badgeSuffix={t('layout.dialogs.fairPrice.dcf.vsMos')}
                >
                    <FairPriceChart />
                </FairPriceVerdict>
            )}
        </>
    );
};
